/* Un fotograma del propio vídeo como carátula, para los vídeos personales (cámara, familia, carpetas sueltas) que no tienen ficha en
   TMDB y se quedaban con un icono gris. Se pide el vídeo a la dirección que ya se usa para verlo (el servidor local de la app, la
   carpeta de casa…), se salta a un punto del 10 % y se pinta en un lienzo pequeño. Si el fotograma sale casi negro (un fundido, una
   pantalla de inicio) se prueba con otro punto. Devuelve una imagen JPEG en texto, o '' si el aparato no puede decodificar ese vídeo. */

export const FRAME_WIDTH = 320;
const DARK_LUMA = 24; // media de brillo (0-255) por debajo de la cual el fotograma se considera «negro»

/** En qué segundos probar: el 10 %, el 30 % y el 55 % (o los puntos que se pidan; nunca antes del segundo 1 ni en el último). */
export function frameTimes(duration, fractions = [0.1, 0.3, 0.55]) {
    const d = Number(duration);
    if (!isFinite(d) || d <= 0) return [1];
    const at = (f) => Math.round(Math.max(1, Math.min(d - 1, d * f)) * 2) / 2;
    return Array.from(new Set(fractions.map(at)));
}

/** ¿El fotograma es casi negro? `rgba` = píxeles del lienzo. Se mira uno de cada 16 (basta para saberlo). */
export function isDark(rgba, threshold = DARK_LUMA) {
    let sum = 0;
    let n = 0;
    for (let i = 0; i + 2 < rgba.length; i += 64) {
        sum += rgba[i] * 0.299 + rgba[i + 1] * 0.587 + rgba[i + 2] * 0.114;
        n++;
    }
    return n === 0 ? true : sum / n < threshold;
}

/** Alto del lienzo que mantiene la proporción del vídeo con el ancho dado (16:9 si no se sabe). */
export function frameHeight(videoWidth, videoHeight, width = FRAME_WIDTH) {
    if (!(videoWidth > 0) || !(videoHeight > 0)) return Math.round((width * 9) / 16);
    return Math.max(1, Math.round((width * videoHeight) / videoWidth));
}

/** Solo el servidor local de la app lleva las cabeceras que dejan leer los píxeles de un vídeo de otro origen. */
export const readableOrigin = (url) => /^https?:\/\/(127\.0\.0\.1|localhost)[:/]/i.test(String(url || ''));

function waitFor(el, events, ms) {
    return new Promise((resolve, reject) => {
        const done = (fn) => (arg) => { cleanup(); fn(arg); };
        const ok = done(resolve);
        const bad = done(() => reject(new Error('video')));
        const timer = setTimeout(done(() => reject(new Error('timeout'))), ms);
        function cleanup() {
            clearTimeout(timer);
            events.forEach((e) => el.removeEventListener(e, ok));
            el.removeEventListener('error', bad);
        }
        events.forEach((e) => el.addEventListener(e, ok, { once: true }));
        el.addEventListener('error', bad, { once: true });
    });
}

/** Fotograma del vídeo en `url` → 'data:image/jpeg;base64,…' o ''. `doc` y `timeoutMs` se pueden cambiar para las pruebas. */
export async function captureFrame(url, { width = FRAME_WIDTH, timeoutMs = 25000, fractions = undefined, doc = typeof document !== 'undefined' ? document : null } = {}) {
    if (!doc || !url) return '';
    const video = doc.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.playsInline = true;
    if (readableOrigin(url)) video.crossOrigin = 'anonymous';
    try {
        video.src = url;
        await waitFor(video, ['loadedmetadata'], timeoutMs);
        const times = frameTimes(video.duration, fractions);
        const canvas = doc.createElement('canvas');
        canvas.width = width;
        canvas.height = frameHeight(video.videoWidth, video.videoHeight, width);
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        for (let i = 0; i < times.length; i++) {
            const seeked = waitFor(video, ['seeked'], timeoutMs);
            video.currentTime = times[i];
            await seeked;
            if (video.readyState < 2) await waitFor(video, ['loadeddata', 'canplay'], timeoutMs);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const px = ctx.getImageData(0, 0, canvas.width, canvas.height).data; // falla si el vídeo es de otro origen sin permiso
            if (!isDark(px) || i === times.length - 1) return canvas.toDataURL('image/jpeg', 0.72);
        }
        return '';
    } catch (e) {
        return '';
    } finally {
        try { video.removeAttribute('src'); video.load(); } catch (e) { /* ya suelto */ }
    }
}

export const COVER_MAX_SIDE = 480;

/** Medidas de una imagen subida: el lado mayor se queda en `max` (nunca se amplía). */
export function coverSize(w, h, max = COVER_MAX_SIDE) {
    if (!(w > 0) || !(h > 0)) return null;
    const k = Math.min(1, max / Math.max(w, h));
    return { width: Math.max(1, Math.round(w * k)), height: Math.max(1, Math.round(h * k)) };
}

/** Imagen elegida por la persona (un fichero del aparato) → 'data:image/jpeg;base64,…' reducida, o '' si no es una imagen legible. */
export async function imageFromFile(file, { max = COVER_MAX_SIDE, doc = typeof document !== 'undefined' ? document : null } = {}) {
    if (!file || !doc || !/^image\//i.test(file.type || '')) return '';
    const url = URL.createObjectURL(file);
    try {
        const img = doc.createElement('img');
        await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = () => reject(new Error('image')); img.src = url; });
        const size = coverSize(img.naturalWidth, img.naturalHeight, max);
        if (!size) return '';
        const canvas = doc.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000'; // las transparencias (PNG) se pintan sobre negro: el JPEG no las admite
        ctx.fillRect(0, 0, size.width, size.height);
        ctx.drawImage(img, 0, 0, size.width, size.height);
        return canvas.toDataURL('image/jpeg', 0.82);
    } catch (e) {
        return '';
    } finally {
        URL.revokeObjectURL(url);
    }
}
